import React from "react";

import { css, cx } from "@emotion/css";
import { InputProps } from "@mui/material/Input";

import { useStyles } from "@/hooks/useStyles.ts";
import { BaseGlycemiaParameters, GlycemiaParametersType } from "@/models/DiabetesDataModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { ThresholdStep } from "@/uiKit/molecules/thresholds/ThresholdStep.tsx";

import {
  ThresholdInputMapping,
  ThresholdStepList,
  allThresholdInputMapping,
  allThresholds,
  gestationalThresholdInputMapping,
  gestationalThresholds,
} from "./ThresholdStepList";

export type ThresholdType = "all" | "gestational";

export type ThresholdSettingsProps = {
  id?: string;
  title?: string;
  type: ThresholdType;
  inputProps?: (threshold: ThresholdStep) => InputProps;
  glycemiaParameters: BaseGlycemiaParameters;
  onSubmit: (threshold: GlycemiaParametersType, value: number) => void;
  className?: string;
};

export const ThresholdSettings: React.FC<ThresholdSettingsProps> = ({
  id,
  title,
  type,
  glycemiaParameters,
  onSubmit,
  inputProps,
  className,
}) => {
  const styles = useStyles(makeStyles);

  return (
    <div className={cx(styles.container, className)}>
      {title ? (
        <Typography variant="subtitle" className={styles.title}>
          {title}
        </Typography>
      ) : undefined}
      <div className={styles.content}>
        <ThresholdStepList
          id={id}
          diabetesParameters={glycemiaParameters}
          {...thresholdsInfo(type)}
          inputProps={inputProps}
          onSubmit={onSubmit}
        />
      </div>
    </div>
  );
};

type ThresholdInfo = {
  thresholds: ThresholdStep[];
  thresholdInputMapping: ThresholdInputMapping;
};

const thresholdsInfo = (type: ThresholdType): ThresholdInfo => {
  switch (type) {
    case "all":
      return {
        thresholds: allThresholds,
        thresholdInputMapping: allThresholdInputMapping,
      };
    case "gestational":
      return {
        thresholds: gestationalThresholds,
        thresholdInputMapping: gestationalThresholdInputMapping,
      };
  }
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
  `,
  content: css`
    display: flex;
  `,
  title: css`
    padding-bottom: ${theme.spacing(4)};
  `,
});
